import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    cueFiles: [],            // selected file paths (batch input)
    selectedFile: null,      // file currently open in the waveform editor
    cuesByFile: {},          // filePath -> CuePoint[]
    durationsByFile: {},     // filePath -> seconds
    errorsByFile: {},        // filePath -> error string
    // filePath -> ms the DJ apps' decoder timeline runs AHEAD of the waveform's
    // (up to 51 on LAME MP3s; scripts/mp3_timeline.py). Cue positions are kept
    // on the apps' timeline; the editor subtracts this to draw/play them.
    timelineOffsetByFile: {},
    // filePath -> {app, median_off_ms, bar_phase} when Generate found the
    // track's Rekordbox/Serato grid off the beat and placed loops on the
    // detected beats instead (beatgrid.repair_app_grid).
    gridRepairByFile: {},
    // Downbeat compare (dev): filePath -> { merged: CuePoint[], kick: CuePoint[] }
    // (raw generated placements per downbeat mode) and which one is shown.
    variantsByFile: {},
    shownVariant: 'merged',
    isProcessing: false,
    progress: 0,
    progressMessage: '',
    cancelRequested: false,
    past: [],                // undo stack of cuesByFile snapshots
    future: [],              // redo stack
};

const cloneCues = (c) => JSON.parse(JSON.stringify(c || {}));

const cuePointsSlice = createSlice({
    name: 'cuePoints',
    initialState,
    reducers: {
        setCueFiles(state, action) {
            state.cueFiles = action.payload || [];
        },
        resetCueFiles(state) {
            state.cueFiles = [];
            state.selectedFile = null;
            state.cuesByFile = {};
            state.variantsByFile = {};
            state.durationsByFile = {};
            state.errorsByFile = {};
            state.timelineOffsetByFile = {};
            state.gridRepairByFile = {};
            state.progress = 0;
            state.progressMessage = '';
        },
        setTimelineOffset(state, action) {
            const { filePath, offsetMs } = action.payload || {};
            if (!filePath) return;
            if (!state.timelineOffsetByFile) state.timelineOffsetByFile = {};
            state.timelineOffsetByFile[filePath] = Number(offsetMs) || 0;
        },
        setSelectedCueFile(state, action) {
            state.selectedFile = action.payload;
        },
        setCuesForFile(state, action) {
            const { filePath, cuePoints } = action.payload;
            state.cuesByFile[filePath] = cuePoints || [];
        },
        // Merge a batch of {filePath, cuePoints, durationSec, error} results.
        setBatchResults(state, action) {
            for (const r of action.payload || []) {
                if (!r || !r.filePath) continue;
                state.cuesByFile[r.filePath] = r.cuePoints || [];
                if (r.durationSec != null) state.durationsByFile[r.filePath] = r.durationSec;
                if (r.timelineOffsetMs != null) {
                    if (!state.timelineOffsetByFile) state.timelineOffsetByFile = {};
                    state.timelineOffsetByFile[r.filePath] = Number(r.timelineOffsetMs) || 0;
                }
                if (!state.gridRepairByFile) state.gridRepairByFile = {};
                if (r.gridRepair) state.gridRepairByFile[r.filePath] = r.gridRepair;
                else if (r.gridSource) delete state.gridRepairByFile[r.filePath];
                if (r.error) state.errorsByFile[r.filePath] = r.error;
                else delete state.errorsByFile[r.filePath];
                if (r.cuePointsByVariant) state.variantsByFile[r.filePath] = r.cuePointsByVariant;
            }
        },
        setShownVariant(state, action) {
            state.shownVariant = ['merged', 'kick', 'structural'].includes(action.payload) ? action.payload : 'merged';
        },
        addCue(state, action) {
            const { filePath, cue } = action.payload;
            if (!state.cuesByFile[filePath]) state.cuesByFile[filePath] = [];
            state.cuesByFile[filePath].push(cue);
            state.cuesByFile[filePath].sort((a, b) => a.positionMs - b.positionMs);
        },
        updateCue(state, action) {
            const { filePath, index, patch } = action.payload;
            const list = state.cuesByFile[filePath];
            if (list && list[index]) {
                list[index] = { ...list[index], ...patch };
                list.sort((a, b) => a.positionMs - b.positionMs);
            }
        },
        removeCue(state, action) {
            const { filePath, index } = action.payload;
            const list = state.cuesByFile[filePath];
            if (list) list.splice(index, 1);
        },
        // Remove ONE track from the batch list (its cues/duration/error go too).
        removeCueFile(state, action) {
            const f = action.payload;
            state.cueFiles = state.cueFiles.filter((x) => x !== f);
            delete state.cuesByFile[f];
            delete state.durationsByFile[f];
            delete state.errorsByFile[f];
            if (state.timelineOffsetByFile) delete state.timelineOffsetByFile[f];
            if (state.gridRepairByFile) delete state.gridRepairByFile[f];
            if (state.selectedFile === f) state.selectedFile = state.cueFiles[0] || null;
        },
        setCueIsProcessing(state, action) {
            state.isProcessing = action.payload;
        },
        setCueProgress(state, action) {
            state.progress = action.payload;
        },
        setCueProgressMessage(state, action) {
            state.progressMessage = action.payload;
        },
        setCueCancelRequested(state, action) {
            state.cancelRequested = action.payload;
        },
        // ---- undo / redo (history of cuesByFile) ----
        pushCueUndo(state, action) {
            state.past.push(action.payload);           // pre-change snapshot (already cloned)
            if (state.past.length > 60) state.past.shift();
            state.future = [];
        },
        clearCueHistory(state) {
            state.past = [];
            state.future = [];
        },
        undoCues(state) {
            if (!state.past.length) return;
            state.future.push(cloneCues(state.cuesByFile));
            state.cuesByFile = state.past.pop();
        },
        redoCues(state) {
            if (!state.future.length) return;
            state.past.push(cloneCues(state.cuesByFile));
            state.cuesByFile = state.future.pop();
        },
    },
});

export const {
    setCueFiles,
    resetCueFiles,
    setSelectedCueFile,
    setCuesForFile,
    setBatchResults,
    addCue,
    updateCue,
    removeCue,
    removeCueFile,
    setCueIsProcessing,
    setCueProgress,
    setCueProgressMessage,
    setCueCancelRequested,
    pushCueUndo,
    clearCueHistory,
    undoCues,
    redoCues,
    setShownVariant,
    setTimelineOffset,
} = cuePointsSlice.actions;

export default cuePointsSlice.reducer;
